import type { MouseEvent } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { formatDistance } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import type { GetDashboardNewEnquiriesQuery } from '@graphql/__generated__/graphql';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  makeRowKeyDownHandler,
  navigateWithModifiers,
} from '@modules/_shared/utils/navigation';
import { useLocale } from '@shared/hooks/locale';

import { DashboardPropertyThumbnail } from '../shared/DashboardPropertyThumbnail';

type DashboardEnquiriesCardItemProps = {
  enquiry: GetDashboardNewEnquiriesQuery['enquiries'][number];
};

export const DashboardEnquiriesCardItem = ({
  enquiry,
}: DashboardEnquiriesCardItemProps) => {
  const { t, dateLocale } = useLocale();
  const navigate = useNavigate();

  const fullName = (
    enquiry.buyer_lead?.user
      ? [enquiry.buyer_lead.user.first_name, enquiry.buyer_lead.user.last_name]
      : [enquiry.first_name, enquiry.last_name]
  )
    .filter(Boolean)
    .join(' ');

  const propertyAddress = enquiry.lot?.property
    ? formatAddress(enquiry.lot.property, ', ')
    : '';
  const imageUrl =
    enquiry.lot?.property?.property_images[0]?.image?.url || null;

  const timeAgo = formatDistance(new Date(enquiry.created_at), new Date(), {
    locale: dateLocale,
    addSuffix: true,
  });
  const enquiryPath = `/dashboard/enquiries/${enquiry.id}`;

  const handleButtonClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    navigateWithModifiers(e, navigate, enquiryPath);
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={event => navigateWithModifiers(event, navigate, enquiryPath)}
      onKeyDown={makeRowKeyDownHandler(enquiryPath, navigate)}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        alignItems: 'center',
        pr: 2,
        minHeight: 64,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: 'action.hover',
          borderColor: 'grey.300',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <DashboardPropertyThumbnail imageUrl={imageUrl} />
      <Box
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          mr: 1,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: 600,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {fullName.trim()}
        </Typography>

        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {propertyAddress}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {timeAgo}
        </Typography>

        <Button
          size="small"
          variant="soft"
          color="primary"
          aria-label={t('Reply to {{name}}', { name: fullName })}
          onClick={handleButtonClick}
          onAuxClick={handleButtonClick}
        >
          {t('Reply')}
        </Button>
      </Box>
    </Box>
  );
};
